import dataService from 'services/dataService';
import {
  API_PATIENTS_PROFILE,
  API_PATIENTS_THRESHOLDS,
  apiPatientDetails,
  apiPatientCharts,
  apiPatientInteractiveTime,
  apiPatientVitalsReport,
} from 'services/dataService/resources';

const INITIAL_STATE = {
  profile: {},
  devices: [],
  charts: {},
  thresholds: {},
  doctor: {},
  clinic: {},
  priorAuthorizations: [],
  insurances: [],
  interactiveTime: {},
  diagnosis: {},
  emr: {},
  dates: [],
};

const patient = {
  state: INITIAL_STATE,
  reducers: {
    reset(state) {
      return {
        ...INITIAL_STATE,
        dates: state.dates.length > 0 ? state.dates : [],
      };
    },
    setDetails(state, payload) {
      return {
        ...state,
        ...payload,
      };
    },
    setCharts(state, payload) {
      return {
        ...state,
        charts: payload,
      };
    },
    setInteractiveTime(state, payload) {
      return {
        ...state,
        interactiveTime: payload,
      };
    },
    setFilterDates(state, payload) {
      return {
        ...state,
        dates: payload,
      };
    },
    setProfile(state, payload) {
      return {
        ...state,
        profile: payload,
      };
    },
  },
  effects: (dispatch) => ({
    async resetPatient() {
      dispatch.patient.reset();
    },
    async getDetails(patientGuid) {
      const { data } = await dataService.getList(
        apiPatientDetails(patientGuid),
      );
      if (data) {
        dispatch.patient.setDetails({
          profile: data.profile || {},
          devices: data.devices || [],
          charts: data.charts || {},
          thresholds: data.thresholds || {},
          doctor: data.doctor || {},
          clinic: data.clinic || {},
          priorAuthorizations: data.priorAuths || [],
          insurances: data.insurances || [],
          diagnosis: data.diagnosis || {},
          emr: data.emr || {},
          careAgents: data.careAgents || [],
        });
      }
    },
    async submit(postData, state) {
      const { error, data } = await dataService.updateOnly(
        API_PATIENTS_PROFILE,
        postData,
      );
      if (!error) {
        dispatch.patient.setDetails({
          profile: {
            ...state.patient.profile,
            ...data,
          },
        });
      }
      return { error, data };
    },
    async submitThresholds(postData, state) {
      const { error, data } = await dataService.updateOnly(
        API_PATIENTS_THRESHOLDS,
        postData,
      );
      if (!error) {
        dispatch.patient.setDetails({
          thresholds: {
            ...state.patient.thresholds,
            ...data,
          },
        });
        const patientGuid = state.patient.profile.guid;
        const { clinicGuid } = state.patient.clinic;

        dispatch.activity.setPage(1);
        dispatch.activity.setReviewed(false);
        dispatch.activity.getActivities({ patientGuid, clinicGuid });
      }
      return { error, data };
    },
    async getCharts({
      patientGuid,
      devices,
      startTime = null,
      endTime = null,
    }) {
      const { data } = await dataService.getList(
        apiPatientCharts(patientGuid, devices, startTime, endTime),
      );
      if (data) {
        const normalizedData = {
          bloodPressure: [],
          bloodGlucose: [],
          weight: [],
          spO2: [],
          temperature: [],
          sleepingMatActivity: [],
          sleepingMatDuration: [],
          peakFlow: [],
        };
        const keys = {
          BloodPressure: 'bloodPressure',
          BloodGlucose: 'bloodGlucose',
          Weight: 'weight',
          SpO2: 'spO2',
          Temperature: 'temperature',
          SleepingMatActivity: 'sleepingMatActivity',
          SleepingMatDuration: 'sleepingMatDuration',
          PeakFlow: 'peakFlow',
        };
        data.forEach((chart) => {
          const currentKey = keys[chart.readingType];
          normalizedData[currentKey]?.push(...chart.data);
        });
        dispatch.patient.setCharts(normalizedData);
      }
    },
    async applyTimeFilter(dates) {
      if (dates.length === 0) {
        dispatch.patient.setFilterDates([]);
      } else {
        const [startDate, endDate] = dates;
        const startTime = startDate.getTime();
        const endTime = endDate.getTime();
        dispatch.patient.setFilterDates([startTime, endTime]);
      }
    },
    async getInteractiveTime(patientGuid) {
      const { data } = await dataService.getList(
        apiPatientInteractiveTime(patientGuid),
      );
      if (data) {
        dispatch.patient.setInteractiveTime(data);
      }
    },
    async getVitalsReport({ patientGuid, type }, state) {
      const timezoneOffset = new Date().getTimezoneOffset();

      const [startTime, endTime] = state.patient.dates;
      const { error, data } = await dataService.getOnly(
        apiPatientVitalsReport(
          patientGuid,
          type,
          startTime,
          endTime,
          timezoneOffset,
        ),
      );
      return { error, data };
    },
  }),
};

export default patient;
